import React, { useEffect, useMemo, useRef, useState } from 'react';
import { planOf, planOfShop } from '../../constants/plans';
import { Link } from 'react-router-dom';
import { useT } from '../../i18n';
import { fetchDesigners } from '../../firebase/designerService';
import { fetchShops } from '../../firebase/shopService';
import { fetchProducts } from '../../firebase/productService';
import ProductGrid from '../ProductGrid';
import PersonIcon from '../PersonIcon';
import './LandingSections.css';

// ── THE TWO PREVIEWS ─────────────────────────────────────────────────────────
// A handful of designers and a handful of products, each ending in a link to
// the page that holds the rest.
//
// A PREVIEW, NOT THE DIRECTORY. The homepage used to carry the whole product
// catalogue — search field, five filters, colour swatches, sort, pager — which
// made a 3D design tool read as a furniture shop. The full thing now lives at
// /designers and /shop; what stays here is enough to show the ecosystem exists
// and a way through to it.
//
// FOUR AND SIX. Both fit one row on a desktop and two on a phone, so neither
// section ever ends on a half-empty row, and neither pushes the final call to
// action further down than it already is.
const DESIGNERS = 4;
const PRODUCTS = 6;

/* ── FEATURED DESIGNERS ────────────────────────────────────────────────────
   The directory, sorted by likes and cut to four.

   `fetchDesigners` is the same module-cached read the rest of the app uses, so
   arriving here from anywhere that already loaded it costs nothing — and going
   on to /designers costs nothing either, because it is the same cache.

   NO RATINGS, because there are none: an account carries a like count and a
   published-work count, and inventing stars out of either would be a number
   that means nothing. Likes are shown when there are any. */
export function LandingDesigners() {
  const { t } = useT();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    fetchDesigners()
      .then(list => { if (alive.current) setRows(list); })
      // An empty section is better than a broken one: the whole block is
      // dropped below rather than rendering an empty rail with a heading.
      .catch(() => { if (alive.current) setRows([]); })
      .finally(() => { if (alive.current) setLoading(false); });
    return () => { alive.current = false; };
  }, []);

  // ── PLAN FIRST, THEN CHANCE ────────────────────────────────────────────────
  // The same rule the recommendations rail and the search results use: gold
  // designers lead, then silver, bronze, free, with the order inside each tier
  // drawn fresh on every visit so nobody on the same tier is permanently ahead.
  //
  // Grouped and shuffled rather than sorted with a random comparator — a
  // comparator that answers differently for the same pair on different
  // comparisons is not a valid ordering and can bias or corrupt the result.
  //
  // An empty tier contributes nothing, so with no gold designers the row simply
  // starts at silver. No special case for "if nobody has gold".
  //
  // This REPLACES the sort by likes. On a page whose job is to show the
  // ecosystem exists, the tier is what the plan is sold for; likes still order
  // the product row below, which no plan applies to.
  const shown = useMemo(() => {
    const shuffled = (arr) => {
      const a = [...arr];
      for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
      }
      return a;
    };

    // ── PAID TIERS ONLY ──────────────────────────────────────────────────────
    // Free designers do not appear in this preview. Being shown on the homepage
    // is part of what a plan buys, which makes this a placement rather than a
    // sample of the directory — the complete list is behind "View all
    // designers" below and still includes everybody.
    //
    // A FILTER, not just a rank. Search ranks free designers lower but still
    // shows them, because a directory that hides accounts is not a directory.
    // This row is not one.
    const byTier = new Map();
    for (const d of rows) {
      const rank = planOf(d).searchRank;
      if (rank === 0) continue;
      if (!byTier.has(rank)) byTier.set(rank, []);
      byTier.get(rank).push(d);
    }

    return [...byTier.keys()]
      .sort((a, b) => b - a)
      .flatMap(rank => shuffled(byTier.get(rank)))
      .slice(0, DESIGNERS);
  }, [rows]);

  // Nothing to preview — the heading and the "view all" link would promise a
  // directory that is empty.
  if (!loading && !shown.length) return null;

  return (
    <section className="lsx lsx-people">
      {/* The heading is centred like every other section's now — the "view
          all" link used to sit on its right, which made these two the only
          sections on the page whose heading was left-aligned. It has moved
          below the cards, where it reads as "and there are more of these"
          rather than as a control competing with the heading. */}
      <div className="lsx-head">
        <h2 className="lsx-h2">{t('lnd_dsg_title')}</h2>
      </div>

      <div className="lsx-people-grid">
        {shown.map(d => {
          const id = d.uid || d.id;
          const facts = [
            d.location,
            d.experienceYears != null && d.experienceYears !== ''
              ? t('ds_years_short').replace('{n}', d.experienceYears) : null,
          ].filter(Boolean);
          return (
            <Link
              key={id}
              /* Tier tint, same as the feed rail and the search results. */
              className={`lsx-person lsx-person--${planOf(d).id}`}
              to={`/designer/${id}`}
            >
              <span className="lsx-person-av">
                {d.avatarUrl ? <img src={d.avatarUrl} alt="" loading="lazy" /> : <PersonIcon />}
              </span>
              <span className="lsx-person-n">{d.fullName}</span>
              {facts.length > 0 && <span className="lsx-person-m">{facts.join(' · ')}</span>}
              {d.likeCount > 0 && (
                <span className="lsx-person-likes">♥ {d.likeCount}</span>
              )}
            </Link>
          );
        })}
      </div>

      <div className="lsx-more">
        <Link className="lsx-all" to="/designers">
          {t('lnd_dsg_all')} <span aria-hidden="true">→</span>
        </Link>
      </div>
    </section>
  );
}

/* ── FEATURED SHOPS ────────────────────────────────────────────────────────
   The shops themselves, above the row of their products.

   GOLD ONLY, and unlike the designer row above it this is not a rank — it is
   the whole condition. `landingPage` is a benefit the gold shop tier is sold
   by name, so a silver or free shop appearing here would be giving away the
   thing that was paid for. The complete directory is behind the link below and
   still lists every shop.

   Shuffled rather than ordered: every shop in here holds the same tier, so
   there is nothing to rank them by that either of them bought. A fresh draw
   per visit is the only fair answer.

   Four, matching the designer row, so the two sections read as a pair. */
export function LandingShops() {
  const { t } = useT();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    fetchShops()
      .then(list => { if (alive.current) setRows(list); })
      .catch(() => { if (alive.current) setRows([]); })
      .finally(() => { if (alive.current) setLoading(false); });
    return () => { alive.current = false; };
  }, []);

  const shown = useMemo(() => {
    const gold = rows.filter(s => planOfShop(s).landingPage);
    for (let i = gold.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [gold[i], gold[j]] = [gold[j], gold[i]];
    }
    return gold.slice(0, DESIGNERS);
  }, [rows]);

  // No gold shops — the heading and the "all shops" link would introduce a
  // section with nothing in it. NOT the same call as the product row below,
  // which falls back to everyone: there the section is about the goods and
  // some are always worth showing, whereas this one is only ever about the
  // shops that bought the placement.
  if (!loading && !shown.length) return null;

  return (
    <section className="lsx lsx-people">
      <div className="lsx-head">
        <h2 className="lsx-h2">{t('lnd_shops_title')}</h2>
      </div>

      <div className="lsx-people-grid">
        {shown.map(s => {
          const id = s.uid || s.id;
          const facts = [s.location, s.delivery ? t('shop_delivery') : null].filter(Boolean);
          return (
            <Link
              key={id}
              /* Always gold here, but written from the plan rather than
                 hardcoded — if the benefit is ever extended to silver, the
                 tint follows without a second edit. */
              className={`lsx-person lsx-person--${planOfShop(s).id}`}
              to={`/shop/${id}`}
            >
              <span className="lsx-person-av">
                {s.avatarUrl ? <img src={s.avatarUrl} alt="" loading="lazy" /> : <PersonIcon />}
              </span>
              <span className="lsx-person-n">{s.name}</span>
              {facts.length > 0 && <span className="lsx-person-m">{facts.join(' · ')}</span>}
              {s.likeCount > 0 && (
                <span className="lsx-person-likes">♥ {s.likeCount}</span>
              )}
            </Link>
          );
        })}
      </div>

      <div className="lsx-more">
        {/* The shop DIRECTORY, which is the search page in shop mode —
            `/shop` is the product marketplace and would land a reader among
            listings when they asked to see shops. */}
        <Link className="lsx-all" to="/search?type=shop">
          {t('lnd_shops_all')} <span aria-hidden="true">→</span>
        </Link>
      </div>
    </section>
  );
}

/* ── SHOP PREVIEW ──────────────────────────────────────────────────────────
   Six listings, newest first, on the SAME tile the marketplace and the search
   page render — so a product looks like itself wherever it is seen. See
   ProductGrid.

   No filters, no sort, no pager: those are what /shop is for. */
export function LandingShop() {
  const { t } = useT();
  const [products, setProducts] = useState([]);
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    Promise.all([fetchProducts(), fetchShops()])
      .then(([p, s]) => { if (alive.current) { setProducts(p); setShops(s); } })
      .catch(() => { if (alive.current) { setProducts([]); setShops([]); } })
      .finally(() => { if (alive.current) setLoading(false); });
    return () => { alive.current = false; };
  }, []);

  const ownerOf = useMemo(() => {
    const m = new Map();
    for (const s of shops) m.set(s.uid || s.id, s);
    return (uid) => m.get(uid);
  }, [shops]);

  const ms = (v) => (typeof v?.toMillis === 'function' ? v.toMillis() : 0);
  // ── ONLY GOLD SHOPS REACH THE HOMEPAGE ─────────────────────────────────────
  // 'Shown on the homepage' is what the gold shop tier sells, so this row is a
  // placement rather than a sample of the marketplace. The full catalogue,
  // every shop included, is behind the link below and stays complete.
  //
  // Filtered by the OWNER, not by the listing: a product has no plan of its
  // own, and the shop is what was sold the placement. An owner missing from the
  // directory is dropped rather than shown — a listing whose shop cannot be
  // resolved has no byline either, and guessing it into a paid slot would be
  // the wrong way to fail.
  //
  // UNLESS NOBODY HAS BOUGHT IT YET, in which case the row falls back to the
  // newest listings from every shop. This does not weaken the benefit: the
  // exclusive placement is exclusive the moment ONE shop takes gold, and until
  // then there is no one to displace. The alternative is a homepage that skips
  // from designers straight to the call to action and looks like a marketplace
  // with nothing in it — which sells the tier to nobody.
  //
  // Note this keys off the GOLD SHOP COUNT, not off the filtered result being
  // empty: a gold shop with no listings yet must still hold the section shut
  // rather than reopening it to everyone.
  const shown = useMemo(() => {
    const gold = new Set(
      shops.filter(sh => planOfShop(sh).landingPage).map(sh => sh.uid || sh.id),
    );
    const pool = gold.size ? products.filter(pr => gold.has(pr.ownerUid)) : products;
    return [...pool]
      .sort((a, b) => ms(b.createdAt) - ms(a.createdAt))
      .slice(0, PRODUCTS);
  }, [products, shops]);

  if (!loading && !shown.length) return null;

  return (
    <section className="lsx lsx-shop">
      <div className="lsx-head">
        <h2 className="lsx-h2">{t('lnd_shop_title')}</h2>
        <p className="lsx-sub">{t('lnd_shop_sub')}</p>
      </div>

      <ProductGrid items={shown} ownerOf={ownerOf} />

      <div className="lsx-more">
        <Link className="lsx-all" to="/shop">
          {t('lnd_shop_all')} <span aria-hidden="true">→</span>
        </Link>
      </div>
    </section>
  );
}
